/**
 * DeleteBody — renders the expanded detail for a `delete` version.
 *
 * Backend audit (Commit 6 audit step):
 *   diff = { kind: 'delete', agent_name, array_key, id_field,
 *            record_id, deleted_record, cascade_deleted }
 *
 *   - Primary diff written at record_operations.py:887-895.
 *   - cascade_deleted entries are built by
 *     _capture_cascade_deletions (lines 1196-1202): each entry
 *     ships {agent_name, array_key, id_field, record_id,
 *     deleted_record}. The full record body is included.
 *   - This means the mockup's `// EXTRA: c.record` annotation was
 *     wrong — the actual field is `deleted_record` and it IS
 *     shipped. (One of the three "mock-only" items the plan called
 *     out has turned out to be live; only baseline/snapshot agent
 *     counts and restore rollup counts remain genuinely mock-only.)
 *
 * Render:
 *   - Caption with record_id + collection
 *   - Strikethrough MiniRow of the primary deleted record
 *   - If cascade_deleted is non-empty, a section with one MiniRow
 *     per cascaded record, using each entry's own agent_name to
 *     pick the right column layout.
 */
import React from 'react';
import { VersionRecord, DeleteDiff } from '../../state/types';
import { agentDisplayName } from '../../utils/operationMeta';
import MiniRow from '../MiniRow/MiniRow';
import styles from './operationBodies.module.css';
import deleteStyles from './DeleteBody.module.css';

interface DeleteBodyProps {
  version: VersionRecord;
}

export default function DeleteBody({ version }: DeleteBodyProps) {
  const diff = version.diff as DeleteDiff | null;
  if (!diff || diff.kind !== 'delete' || !version.agent_name) return null;

  const cascade = diff.cascade_deleted ?? [];

  return (
    <div className={styles.body}>
      <div className={styles.detailHeader}>
        <span>
          Removed <code>{diff.record_id}</code>
        </span>
        {diff.array_key && (
          <span>
            Collection <code>{diff.array_key}</code>
          </span>
        )}
      </div>
      <MiniRow
        agentName={version.agent_name}
        record={diff.deleted_record}
        state="removed"
      />

      {cascade.length > 0 && (
        <div className={deleteStyles.cascadeBlock}>
          <div className={deleteStyles.cascadeLabel}>
            Cascade · {cascade.length} dependent record
            {cascade.length === 1 ? '' : 's'} removed
          </div>
          <div className={deleteStyles.cascadeRows}>
            {cascade.map((c) => (
              <div
                key={`${c.agent_name}:${c.record_id}`}
                className={deleteStyles.cascadeGroup}
              >
                <div className={deleteStyles.cascadeMeta}>
                  via {agentDisplayName(c.agent_name)} ·{' '}
                  <code>{c.record_id}</code>
                </div>
                <MiniRow
                  agentName={c.agent_name}
                  record={c.deleted_record}
                  state="removed"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
